import { gql } from '@apollo/client';
import { RCAutocomplete, RCFormGroup } from '@reverbdotcom/cadence/components';
import I18n from 'i18n-js';
import { RCAutocompleteItem } from '@reverbdotcom/cadence/components/RCAutocomplete/RCAutocompleteItem';
import React from 'react';
import { useLazyQuery } from '@reverbdotcom/commons/src/useLazyQuery';
import {
  CoreAddressAutocompleteFindAddressSuggestions,
  CoreAddressAutocompleteSelectAddressSuggestion,
  core_apimessages_AddressEntry as AddressEntry,
  rql_AddressSuggestion as AddressSuggestion,
  rql_AddressSuggestion_Type as AddressSuggestionType,
} from '../schemas/rql';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';

interface Props {
  onAddressSelect(address: AddressEntry): void;
  onChooseManualEntry(): void;
}

export function AddressAutocomplete(props: Props) {
  const [isOpen, setOpen] = React.useState<boolean>(false);
  const [inputValue, setInputValue] = React.useState('');

  const [selectSuggestion] = useLazyQuery<
    CoreAddressAutocompleteSelectAddressSuggestion.Query,
    CoreAddressAutocompleteSelectAddressSuggestion.Variables
  >(
    SELECT_SUGGESTION_QUERY,
  );

  const [findSuggestions, suggestionsResult] = useLazyQuery<
    CoreAddressAutocompleteFindAddressSuggestions.Query,
    CoreAddressAutocompleteFindAddressSuggestions.Variables
  >(
    FIND_SUGGESTIONS_QUERY,
  );

  const showLoading = suggestionsResult.called && suggestionsResult.loading;
  const suggestions = suggestionsResult.data?.findAddressSuggestions?.suggestions || [];

  const showManualEntry = inputValue.length > 2 || suggestions.length > 1;
  const user = useUser();

  function onQueryChange(value: string) {
    setInputValue(value);
    findSuggestions({ variables: { query: value, countryCode: user.countryCode } });
  }

  async function onSuggestionSelect(suggestion: AddressSuggestion) {
    if (suggestion.type === AddressSuggestionType.CONTAINER) {
      findSuggestions({ variables: { containerId: suggestion.id, countryCode: user.countryCode } });
    } else if (suggestion.type === AddressSuggestionType.ADDRESS) {
      const result = await selectSuggestion({ variables: { id: suggestion.id } });
      const address = result.data?.selectAddressSuggestion?.address;

      if (address) {
        setOpen(false);
        props.onAddressSelect(address);
      }
    }
  }

  return (
    <RCFormGroup
      inputId="address-autocomplete-input"
      label={I18n.t('discovery.addressAutocomplete.label')}
    >
      <RCAutocomplete
        inputValue={inputValue}
        inputId="address-autocomplete-input"
        onInputChange={onQueryChange}
        loading={showLoading}
        isOpen={isOpen}
        onOpenChange={setOpen}
        placeholder={I18n.t('discovery.addressAutocomplete.placeholder')}
      >
        {inputValue.length !== 0 &&
          <RCAutocompleteItem
            key="keep-typing"
            id="keep-typing"
            title={I18n.t('discovery.addressAutocomplete.continueTyping')}
            variant="disabled"
          />
        }
        {suggestions.length > 0 &&
          suggestions.map((suggestion) => (
            <RCAutocompleteItem
              key={suggestion.id}
              id={suggestion.id}
              title={suggestion.text}
              type={suggestion.type}
              onClick={() => onSuggestionSelect(suggestion)}
              variant={suggestion.type === AddressSuggestionType.CONTAINER ? 'parent' : undefined}
            />
          ))
        }
        {showManualEntry &&
          <RCAutocompleteItem
            key="manual-entry"
            id="manual-entry"
            title={I18n.t('discovery.addressAutocomplete.manualEntry')}
            variant="link"
            onClick={() => props.onChooseManualEntry()}
          />
        }
      </RCAutocomplete>
    </RCFormGroup>
  );
}

export const FIND_SUGGESTIONS_QUERY = gql`
  query Core_AddressAutocomplete_FindAddressSuggestions(
    $query: String,
    $containerId: String,
    $countryCode: String
  ) {
    findAddressSuggestions(input: { text: $query, container: $containerId, countryCode: $countryCode }) {
      suggestions {
        id
        type
        text
      }
    }
  }
`;

export const SELECT_SUGGESTION_QUERY = gql`
  query Core_AddressAutocomplete_SelectAddressSuggestion($id: String) {
    selectAddressSuggestion(input: { id: $id }) {
      id
      address {
        streetAddress
        extendedAddress
        locality
        region
        postalCode
        countryCode
      }
    }
  }
`;
